import { formatNumberGroupingSeparators } from '@vmos2/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@vmos2/design-system';
import styled from '@emotion/styled';

import { LoyaltyLogo } from './loyaltyLogo';
import { LoyaltyColor } from './loyaltyColor';
import { selectSlug } from '@vmos2/utils';

type Props = {
  className?: string | null,
  isPayingWithPoints?: boolean,
  orderUUID?: string,
  slug: string,
  disabled?: boolean,
  pointsEarnedForOrder: number,
  currentRewardsPoints?: number,
  checkBoxOnChange?: Function | null,
  hasEnoughPoints: boolean,
  isApplying: boolean,
  showLoyaltyWithPromotions: boolean,
  isCalculating: boolean,
};
const LoyaltyPoints = ({
  className,
  pointsEarnedForOrder,
  currentRewardsPoints,
  isPayingWithPoints,
  disabled,
  checkBoxOnChange,
  hasEnoughPoints,
  orderUUID,
  slug,
  isApplying,
  showLoyaltyWithPromotions,
  isCalculating,
}: Props) => {
  const { t } = useTranslation('loyalty');
  const { t: tCommon } = useTranslation('common');
  const points = (hasEnoughPoints && currentRewardsPoints) || pointsEarnedForOrder;
  const translationKey = hasEnoughPoints ? 'currentPoints' : orderUUID ? 'earnedPoints' : 'earnPoints';
  let showActivation = !!checkBoxOnChange && hasEnoughPoints;

  let headerText = t(translationKey, {
    count: points,
    points: formatNumberGroupingSeparators(points),
    slug: selectSlug(slug, tCommon('point', { count: points })),
  });

  if (disabled && showLoyaltyWithPromotions && !isCalculating) {
    showActivation = false;

    headerText = t('earnPoints', {
      count: pointsEarnedForOrder,
      points: formatNumberGroupingSeparators(pointsEarnedForOrder),
      slug: selectSlug(slug, tCommon('point', { count: pointsEarnedForOrder })),
    });
  }

  return (
    <Wrapper className={className} center={!showActivation}>
      <LogoWrapper size="24px" />
      <Content disabled={disabled && !showLoyaltyWithPromotions} data-test="loyalty-points-content">
        <Header>
          <LoyaltyColor data-test="loyalty-balance">{headerText}</LoyaltyColor>
        </Header>
        {showActivation && (
          <>
            <Description data-test="loyalty-description">
              {t('currentPointsDescription', { slug: selectSlug(slug, tCommon('point', { context: 'plural' })) })}
            </Description>
            <Label disabled={isApplying}>
              <Checkbox
                label={t('payUsingPoints', { slug: selectSlug(slug, tCommon('point', { context: 'plural' })) })}
                name="usePoints"
                onChange={checkBoxOnChange}
                checked={isPayingWithPoints}
                id="loyalty-points"
                data-test="loyalty-use-points-checkbox"
              />
            </Label>
          </>
        )}
      </Content>
    </Wrapper>
  );
};

LoyaltyPoints.defaultProps = {
  className: null,
  disabled: false,
  currentRewardsPoints: 0,
  hasEnoughPoints: false,
  checkBoxOnChange: null,
  isPayingWithPoints: false,
  enableEarningWithLoyalty: false,
};

const Label = styled('label')`
  display: flex;
  font-size: ${({ theme }) => theme.fontSize.md};
  align-items: center;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? '0.3' : '1')};
`;

const Description = styled('div')`
  font-size: ${({ theme }) => theme.fontSize.md};
  margin-top: 8px;
  margin: 8px 0 16px;
`;

const Content = styled('div')`
  ${({ disabled }) =>
    disabled &&
    `
    pointer-events:none;
    opacity: 0.3;
  `}
`;

const LogoWrapper = styled(LoyaltyLogo)`
  margin: 0 16px 0 0;

  @media (${({ theme }) => theme.breakpoint.desktop}) {
    width: 48px;
    height: 48px;
  }
`;

const Wrapper = styled('div')`
  display: flex;
  width: 100%;
  align-items: ${({ center }) => (center ? 'center' : 'flex-start')};
`;

const Header = styled('h3')`
  font-size: ${({ theme }) => theme.fontSize.lg};
  margin: 0;
`;

export default LoyaltyPoints;
